import { ArrowUpRight } from "lucide-react";
import { useBrain } from "@/contexts/ui/brain";
import { Agent } from "@/types/agent";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface GenieKnowledgeCoachCardProps {
  genie: Agent;
  documents: DocumentWithMetadata[];
}

interface QuickPrompt {
  title: string;
  hint: string;
  build: (ctx: PromptContext) => string;
}

interface PromptContext {
  genieName: string;
  genieRole: string;
  docList: string;
  docCount: number;
}

const QUICK_PROMPTS: QuickPrompt[] = [
  {
    title: "What's missing?",
    hint: "Gap analysis",
    build: ({ genieName, genieRole, docList, docCount }) =>
      [
        `Audit the knowledge base for my "${genieName}"${genieRole ? ` (${genieRole})` : ""} genie. Cross-reference what it knows with what its callers have actually been asking. Tell me the top gaps — questions it can't confidently answer today.`,
        "",
        `## What this genie currently knows (${docCount} document${docCount === 1 ? "" : "s"})`,
        docList || "_(no documents attached yet)_",
        "",
        "Format: each gap on its own line, with (1) the missing topic, (2) why it matters, (3) the first sentence of a draft article to fix it.",
      ].join("\n"),
  },
  {
    title: "Suggest 3 docs to add",
    hint: "Prioritised list",
    build: ({ genieName, genieRole, docList }) =>
      [
        `Based on what "${genieName}"${genieRole ? ` (${genieRole})` : ""} does and what its callers ask, recommend the 3 highest-impact documents I should add to its knowledge base next.`,
        "",
        "## What it already has",
        docList || "_(nothing attached yet — start from scratch)_",
        "",
        "For each recommendation, give me: a document title, a 2-3 sentence description of what should be in it, and why this one matters more than other options.",
      ].join("\n"),
  },
  {
    title: "Draft an FAQ",
    hint: "Ready to upload",
    build: ({ genieName, genieRole, docList }) =>
      [
        `Draft a complete FAQ document for my "${genieName}"${genieRole ? ` (${genieRole})` : ""} genie that I can paste straight into the knowledge base.`,
        "",
        "Pull the most common questions its callers have actually been asking (look at recent conversations). Write 8-12 question-and-answer pairs in the genie's voice. Keep answers short and concrete — 2-3 sentences each.",
        "",
        "## Existing docs (don't duplicate these)",
        docList || "_(no existing docs)_",
      ].join("\n"),
  },
  {
    title: "Audit what's there",
    hint: "Quality check",
    build: ({ genieName, docList, docCount }) =>
      [
        `Audit the ${docCount} document${docCount === 1 ? "" : "s"} currently in "${genieName}"'s knowledge base. For each one, tell me:`,
        "",
        "1. Is it being used in real conversations? (How often does the genie cite it?)",
        "2. Is it well-written and well-structured for a voice genie to pull from?",
        "3. Does it contradict anything else in the knowledge base?",
        "4. Verdict: keep as-is / rewrite / merge with another / delete.",
        "",
        "## Documents to audit",
        docList,
      ].join("\n"),
  },
];

const buildHeroPrompt = (ctx: PromptContext): string => {
  const { genieName, genieRole, docList, docCount } = ctx;
  return [
    `Coach my "${genieName}"${genieRole ? ` (${genieRole})` : ""} genie on its knowledge base. Look at what it currently knows, look at what its callers have been asking, and tell me exactly what to do next so it handles future calls better.`,
    "",
    `## What this genie currently knows (${docCount} document${docCount === 1 ? "" : "s"})`,
    docList || "_(no documents attached yet — this genie is starting from scratch)_",
    "",
    "## What I want from you",
    "Give me concrete, prioritised recommendations:",
    "1. **Knowledge gaps** — questions its callers asked that it couldn't confidently answer. Draft a first paragraph for each missing article.",
    "2. **Content to add next** — the 3 highest-impact new documents to upload, with titles and what should be in them.",
    "3. **Existing content to fix** — anything that looks stale, redundant, contradictory, or poorly structured for a voice genie.",
    "4. **Organisation** — should this knowledge be folder-grouped, tagged, or split differently?",
    "",
    "Be blunt. Quote from real conversations when it helps. Skip anything that already looks fine.",
  ].join("\n");
};

const formatDocList = (documents: DocumentWithMetadata[]): string => {
  if (documents.length === 0) return "";
  return documents
    .slice(0, 40)
    .map((doc) => `- ${doc.name}${doc.metadata?.description ? ` — ${doc.metadata.description}` : ""}`)
    .join("\n");
};

export const GenieKnowledgeCoachCard = ({ genie, documents }: GenieKnowledgeCoachCardProps) => {
  const { openBrain } = useBrain();

  const genieName = genie.name || "this genie";
  const genieRole = genie.category_data?.label
    ? String(genie.category_data.label)
    : genie.category || genie.description || "";
  const docList = formatDocList(documents);
  const docCount = documents.length;

  const ctx: PromptContext = { genieName, genieRole, docList, docCount };

  const handleHero = () => {
    openBrain(buildHeroPrompt(ctx));
  };

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-md mb-6">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 py-5"
          style={{
            background:
              "linear-gradient(135deg, rgba(78,156,255,0.08) 0%, rgba(255,77,184,0.08) 100%)",
          }}
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-shrink-0">
              <GenieIcon
                name="lightbulb"
                size={28}
                color="#4E9CFF"
                secondaryColor="#FF4DB8"
                gradientEnabled
                label="Coach this genie's knowledge"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-bold text-gray-900 leading-snug">
                Coach {genieName}'s knowledge
              </h3>
              <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                Your Genie will read this knowledge base alongside recent conversations and tell you
                exactly what to add, fix, or remove so {genieName} handles the next call better.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleHero}
            className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-sm hover:shadow-md hover:opacity-95 transition-all"
            style={{
              background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)",
            }}
          >
            Coach this genie's knowledge
          </button>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {QUICK_PROMPTS.map((p) => (
              <button
                key={p.title}
                type="button"
                onClick={() => openBrain(p.build(ctx))}
                className="group relative text-left rounded-lg bg-white/70 backdrop-blur-sm border border-white hover:border-transparent hover:shadow-sm hover:shadow-genie-primary/15 transition-all px-3 py-2"
                title={p.hint}
              >
                <span
                  className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                  style={{
                    padding: "1px",
                    background: "linear-gradient(135deg, #4E9CFF 0%, #FF4DB8 100%)",
                    WebkitMask:
                      "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                  }}
                />
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-gray-900 leading-snug line-clamp-2 group-hover:bg-gradient-to-r group-hover:from-[#4E9CFF] group-hover:to-[#FF4DB8] group-hover:bg-clip-text group-hover:text-transparent transition-colors">
                      {p.title}
                    </div>
                    <div className="text-[10px] text-gray-400 mt-0.5 truncate">{p.hint}</div>
                  </div>
                  <ArrowUpRight className="w-3 h-3 text-gray-300 group-hover:text-[#FF4DB8] flex-shrink-0 mt-0.5" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenieKnowledgeCoachCard;
